import { useAuthenticator } from '@aws-amplify/ui-react';
import { hasSignerSession } from './signer-session';

/**
 * Unified authentication status across BOTH session backends (#2955):
 *  - Cognito/Amplify (admins + SSO signers) via `useAuthenticator().authStatus`
 *  - the self-issued email-only signer JWT in sessionStorage
 *
 * Returns Amplify's three-state status, but reports `'authenticated'` whenever a
 * valid signer session is present even though Amplify itself is
 * `'unauthenticated'` (the signer never had a Cognito session). The
 * `'configuring'` state is preserved only for the Cognito path — a signer
 * session resolves synchronously, so it is never `'configuring'`.
 */
export type AuthStatus = 'configuring' | 'authenticated' | 'unauthenticated';

export function useAuthStatus(): AuthStatus {
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);

    if (authStatus === 'authenticated') {
        return 'authenticated';
    }
    // A valid signer JWT authenticates regardless of the Cognito state.
    if (hasSignerSession()) {
        return 'authenticated';
    }
    return authStatus as AuthStatus;
}
